import { Zip, ZipDeflate, ZipPassThrough, strToU8 } from "fflate";

import { toUint8Array } from "@/lib/binary";

/**
 * Streaming ZIP writer.
 *
 * JSZip cannot build large archives, on either side:
 *
 *  - Output: `generateAsync({ type: "blob" })` buffers every chunk and then
 *    calls `new Uint8Array(totalLength)` for the whole archive in one
 *    contiguous allocation, which throws `RangeError: Array buffer allocation
 *    failed` on a multi-GB pack.
 *  - Input: a `File` handed to `zip.file()` is read eagerly (all of them, in
 *    one microtask drain) and each entry's decoded bytes are retained for the
 *    lifetime of the zip object — so the whole pack ends up resident no matter
 *    how it is fed in.
 *
 * fflate streams both ways: we pull each File chunk by chunk and push archive
 * chunks straight to a sink, so peak memory is a couple of chunks regardless
 * of pack size.
 */

/** Where streamed archive bytes go. Implemented by the disk and Blob sinks below. */
export type ZipSink = {
  write(chunk: Uint8Array): Promise<void> | void;
  close(): Promise<void> | void;
  abort?(reason?: unknown): Promise<void> | void;
};

export type ZipProgress = {
  /** 0-100, by bytes read from the source files. */
  percent: number;
  bytesWritten: number;
  currentFile: string | null;
  entriesDone: number;
  entriesTotal: number;
};

export type ZipEntry = {
  path: string;
  /** A File/Blob is streamed from disk; strings and byte arrays are already in memory. */
  source: Blob | string | Uint8Array;
  /** Defaults to `shouldDeflate(path)`. */
  deflate?: boolean;
};

/**
 * fflate writes a classic 22-byte end-of-central-directory record with 16-bit
 * counts and 32-bit offsets — there is no ZIP64 write path (same as JSZip).
 * Past these limits it would emit a silently corrupt archive, so refuse first.
 */
export const ZIP_MAX_BYTES = 4 * 1024 ** 3 - 1;
export const ZIP_MAX_ENTRIES = 0xffff;

export function assertZipWritable({ entries, bytes }: { entries: number; bytes: number }) {
  if (bytes > ZIP_MAX_BYTES) {
    throw new Error(
      `This pack is ${(bytes / 1024 ** 3).toFixed(1)} GB. ZIP files built in the browser are capped at 4 GB — split the pack and convert it in batches.`,
    );
  }
  if (entries > ZIP_MAX_ENTRIES) {
    throw new Error(
      `This pack has ${entries.toLocaleString()} files. A ZIP can hold at most ${ZIP_MAX_ENTRIES.toLocaleString()} files — split the pack and convert it in batches.`,
    );
  }
}

/**
 * Extensions whose contents are already compressed. Deflating them costs real
 * time and returns nothing: 160 MB of .ydd/.ytd measured at 160.0 MB -> 160.1 MB
 * in 4.8s, i.e. the archive got *bigger*.
 */
const ALREADY_COMPRESSED = new Set([
  "ydd", "ytd", "yft", "ydr", "yld", "ycd", "ynv", "ybn", "awc", "rpf",
  "dds", "png", "jpg", "jpeg", "webp", "gif",
]);

export function shouldDeflate(path: string): boolean {
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  return !ALREADY_COMPRESSED.has(ext);
}

/**
 * Read a Blob/File in bounded chunks. Prefers `Blob.stream()`, falling back to
 * slicing for environments without it (Safari < 14.1, jsdom). Either way the
 * whole file is never materialised at once.
 */
async function* readBlobChunks(blob: Blob, chunkSize = 1024 * 1024): AsyncGenerator<Uint8Array> {
  if (typeof blob.stream === "function") {
    const reader = blob.stream().getReader();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value?.length) yield toUint8Array(value);
      }
    } finally {
      reader.releaseLock();
    }
    return;
  }

  for (let offset = 0; offset < blob.size; offset += chunkSize) {
    const slice = blob.slice(offset, Math.min(offset + chunkSize, blob.size));
    yield new Uint8Array(await slice.arrayBuffer());
  }
}

function sourceSize(source: ZipEntry["source"]): number {
  if (typeof source === "string") return source.length;
  if (source instanceof Blob) return source.size;
  return source.byteLength;
}

/**
 * Write `entries` into `sink` as a ZIP, streaming each source rather than
 * buffering it. Resolves with the number of archive bytes written.
 */
export async function streamZipEntries(
  entries: ZipEntry[],
  sink: ZipSink,
  options: { onProgress?: (progress: ZipProgress) => void } = {},
): Promise<number> {
  let bytesWritten = 0;
  let bytesRead = 0;
  const totalBytes = entries.reduce((sum, entry) => sum + sourceSize(entry.source), 0);

  // fflate hands us archive chunks synchronously from inside push()/end(), so
  // we buffer them here and drain to the (async) sink between pushes. That
  // gives natural backpressure: the next source chunk is only read once the
  // previous archive chunk has landed.
  const outbox: Uint8Array[] = [];
  let streamError: unknown = null;

  const zip = new Zip((err, chunk, _final) => {
    if (err) {
      streamError ??= err;
      return;
    }
    if (chunk?.length) outbox.push(chunk);
  });

  const drain = async () => {
    while (outbox.length) {
      const chunk = outbox.shift()!;
      await sink.write(chunk);
      bytesWritten += chunk.length;
    }
    if (streamError) throw streamError;
  };

  try {
    let entriesDone = 0;
    for (const entry of entries) {
      const deflate = entry.deflate ?? shouldDeflate(entry.path);
      const handle = deflate ? new ZipDeflate(entry.path, { level: 6 }) : new ZipPassThrough(entry.path);
      zip.add(handle);

      if (entry.source instanceof Blob) {
        for await (const chunk of readBlobChunks(entry.source)) {
          handle.push(chunk, false);
          bytesRead += chunk.length;
          await drain();
          options.onProgress?.({
            percent: totalBytes ? Math.min(100, (bytesRead / totalBytes) * 100) : 100,
            bytesWritten,
            currentFile: entry.path,
            entriesDone,
            entriesTotal: entries.length,
          });
        }
        handle.push(new Uint8Array(0), true);
      } else {
        const bytes = typeof entry.source === "string" ? strToU8(entry.source) : toUint8Array(entry.source);
        handle.push(bytes, true);
        bytesRead += bytes.length;
      }

      await drain();
      entriesDone += 1;
      options.onProgress?.({
        percent: totalBytes ? Math.min(100, (bytesRead / totalBytes) * 100) : 100,
        bytesWritten,
        currentFile: entry.path,
        entriesDone,
        entriesTotal: entries.length,
      });
    }

    zip.end();
    await drain();
    await sink.close();
    return bytesWritten;
  } catch (error) {
    try {
      zip.terminate();
    } catch {
      /* already finished */
    }
    await Promise.resolve(sink.abort?.(error)).catch(() => {});
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Buffers into a handful of large Blob parts rather than one typed array.
 * Blob data lives in the browser's blob store (which can spill to disk), so
 * this never needs a big contiguous allocation on the JS heap.
 */
export function createBlobSink({
  mimeType = "application/zip",
  partSize = 8 * 1024 * 1024,
}: { mimeType?: string; partSize?: number } = {}) {
  const parts: Blob[] = [];
  let pending: Uint8Array<ArrayBuffer>[] = [];
  let pendingBytes = 0;

  const flush = () => {
    if (!pendingBytes) return;
    parts.push(new Blob(pending));
    pending = [];
    pendingBytes = 0;
  };

  return {
    write(chunk: Uint8Array) {
      pending.push(toUint8Array(chunk));
      pendingBytes += chunk.length;
      if (pendingBytes >= partSize) flush();
    },
    close() {
      flush();
    },
    abort() {
      parts.length = 0;
      pending = [];
      pendingBytes = 0;
    },
    partCount: () => parts.length,
    result: () => new Blob(parts, { type: mimeType }),
  };
}

/* ------------------------------------------------------------------ */
/* File System Access API — write straight to disk, constant memory.    */
/* ------------------------------------------------------------------ */

type WritableFileStream = {
  write(data: BufferSource): Promise<void>;
  close(): Promise<void>;
  abort?(reason?: unknown): Promise<void>;
};

type SaveFileHandle = { createWritable(): Promise<WritableFileStream> };

type SaveFilePickerWindow = Window & {
  showSaveFilePicker?: (options: {
    suggestedName?: string;
    types?: Array<{ description: string; accept: Record<string, string[]> }>;
  }) => Promise<SaveFileHandle>;
};

export function canSaveToDisk() {
  return typeof window !== "undefined" && typeof (window as SaveFilePickerWindow).showSaveFilePicker === "function";
}

export type ZipSaveTarget =
  /** Stream straight to the file the user picked. */
  | { kind: "disk"; handle: SaveFileHandle }
  /** No File System Access API — fall back to a Blob download. */
  | { kind: "download" }
  /** User dismissed the save dialog. */
  | { kind: "cancelled" };

/**
 * Must be called from a user gesture (the picker consumes it), so call this
 * before any other await in the click handler.
 */
export async function pickZipSaveTarget(suggestedName: string): Promise<ZipSaveTarget> {
  if (!canSaveToDisk()) return { kind: "download" };
  try {
    const handle = await (window as SaveFilePickerWindow).showSaveFilePicker!({
      suggestedName,
      types: [{ description: "ZIP archive", accept: { "application/zip": [".zip"] } }],
    });
    return { kind: "disk", handle };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return { kind: "cancelled" };
    throw error;
  }
}

export function createFileHandleSink(writable: WritableFileStream): ZipSink {
  return {
    write: (chunk) => writable.write(toUint8Array(chunk)),
    close: () => writable.close(),
    abort: async (reason) => {
      try {
        await writable.abort?.(reason);
      } catch {
        /* the stream may already be torn down */
      }
    },
  };
}

export type ZipWriteResult = {
  /** True when bytes went straight to the user's chosen file. */
  savedToDisk: boolean;
  /** Object URL for the fallback path; null when already saved to disk. */
  url: string | null;
  size: number;
};

/**
 * Write `entries` to the picked file, or to a disk-backed Blob the caller can
 * offer as a download.
 */
export async function writeZip(
  entries: ZipEntry[],
  {
    target,
    onProgress,
  }: { target: ZipSaveTarget; onProgress?: (progress: ZipProgress) => void },
): Promise<ZipWriteResult> {
  if (target.kind === "cancelled") throw new Error("Save cancelled.");

  if (target.kind === "disk") {
    const writable = await target.handle.createWritable();
    const size = await streamZipEntries(entries, createFileHandleSink(writable), { onProgress });
    return { savedToDisk: true, url: null, size };
  }

  const sink = createBlobSink();
  const size = await streamZipEntries(entries, sink, { onProgress });
  return { savedToDisk: false, url: URL.createObjectURL(sink.result()), size };
}
